
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent } from '@/components/ui/card';
import { CalendarIcon, Download, Save, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { ExportTemplate } from './types';

interface EnhancedCertificateExportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EnhancedCertificateExport({ open, onOpenChange }: EnhancedCertificateExportProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>('standard');
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx' | 'pdf' | 'regulatory'>('csv');
  const [filters, setFilters] = useState({
    state: undefined as string | undefined,
    courseType: undefined as string | undefined,
    startDate: format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    isUsed: undefined as boolean | undefined,
    isVerified: undefined as boolean | undefined,
    isReported: undefined as boolean | undefined,
  });
  const [regulatorySettings, setRegulatorySettings] = useState({
    includePII: true,
    includeStudentAddress: true,
    includeCompletionStatus: true,
    formatVersion: '2.1',
    signatureRequired: true,
    signatureText: 'I certify this report to be accurate and complete',
  });
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // Get saved export templates
  const { data: savedTemplates } = useQuery({
    queryKey: ['exportTemplates'],
    queryFn: async () => {
      // Use any() to work around the type constraints
      const { data, error } = await supabase
        .from('export_templates')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data as ExportTemplate[] || [];
    },
    enabled: open && activeTab === 'templates',
  });

  const handleExport = async () => {
    // Simulate export process
    toast({
      title: 'Export Started',
      description: `Exporting certificates as ${exportFormat.toUpperCase()}`,
    });
    
    // Close the dialog
    setTimeout(() => {
      onOpenChange(false);
    }, 1500);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast({
        variant: 'destructive',
        title: 'Template name required',
        description: 'Please provide a name for your export template.',
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('export_templates')
        .insert([
          {
            name: templateName,
            format: exportFormat,
            filters: filters,
            regulatory_settings: activeTab === 'regulatory' ? regulatorySettings : null,
            created_by: (await supabase.auth.getUser()).data?.user?.id
          }
        ]);

      if (error) throw error;

      toast({
        title: 'Template saved',
        description: 'Your export template has been saved successfully.',
      });

      setSaveTemplate(false);
      setTemplateName('');
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Failed to save template',
        description: error.message || 'An unknown error occurred',
      });
    }
  };

  const handleLoadTemplate = async (templateId: string) => {
    try {
      const { data, error } = await supabase
        .from('export_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (error) throw error;
      
      // Apply the template
      const template = data as ExportTemplate;
      if (template) {
        setExportFormat(template.format || 'csv');
        
        // Update filters safely with proper type handling
        if (template.filters) {
          setFilters({
            state: template.filters.state,
            courseType: template.filters.courseType,
            startDate: template.filters.startDate || format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'),
            endDate: template.filters.endDate || format(new Date(), 'yyyy-MM-dd'),
            isUsed: template.filters.isUsed,
            isVerified: template.filters.isVerified,
            isReported: template.filters.isReported,
          });
        }
        
        if (template.regulatory_settings) {
          setRegulatorySettings(template.regulatory_settings);
          setActiveTab('regulatory');
        } else {
          setActiveTab('standard');
        }
        
        toast({
          title: 'Template loaded',
          description: `Template "${template.name}" applied successfully.`,
        });
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Failed to load template',
        description: error.message || 'An unknown error occurred',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Export Certificates</DialogTitle>
          <DialogDescription>
            Export certificate records in various formats with customizable filters
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3">
            <TabsTrigger value="standard">Standard Export</TabsTrigger>
            <TabsTrigger value="regulatory">Regulatory Report</TabsTrigger>
            <TabsTrigger value="templates">Saved Templates</TabsTrigger>
          </TabsList>
          
          <TabsContent value="standard" className="space-y-4">
            {/* Standard Export Options */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="exportFormat">Export Format</Label>
                <Select 
                  value={exportFormat} 
                  onValueChange={(value) => setExportFormat(value as any)}
                >
                  <SelectTrigger id="exportFormat">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Export Format</SelectLabel>
                      <SelectItem value="csv">CSV (Comma Separated)</SelectItem>
                      <SelectItem value="xlsx">Excel Spreadsheet</SelectItem>
                      <SelectItem value="pdf">PDF Document</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="stateFilter">State Filter</Label>
                <Select 
                  value={filters.state || ''} 
                  onValueChange={(value) => setFilters({...filters, state: value || undefined})}
                >
                  <SelectTrigger id="stateFilter">
                    <SelectValue placeholder="All States" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All States</SelectItem>
                    <SelectItem value="FL">Florida</SelectItem>
                    <SelectItem value="CA">California</SelectItem>
                    <SelectItem value="TX">Texas</SelectItem>
                    <SelectItem value="NY">New York</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="courseType">Course Type</Label>
                <Select 
                  value={filters.courseType || ''} 
                  onValueChange={(value) => setFilters({...filters, courseType: value || undefined})}
                >
                  <SelectTrigger id="courseType">
                    <SelectValue placeholder="All Course Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Course Types</SelectItem>
                    <SelectItem value="BDI">BDI</SelectItem>
                    <SelectItem value="ADI">ADI</SelectItem>
                    <SelectItem value="TLSAE">TLSAE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="usageFilter">Usage Status</Label>
                <Select 
                  value={filters.isUsed === undefined ? '' : String(filters.isUsed)} 
                  onValueChange={(value) => {
                    const isUsed = value === '' ? undefined : value === 'true';
                    setFilters({...filters, isUsed});
                  }}
                >
                  <SelectTrigger id="usageFilter">
                    <SelectValue placeholder="Any Usage Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any Usage Status</SelectItem>
                    <SelectItem value="true">Used Only</SelectItem>
                    <SelectItem value="false">Unused Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !filters.startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.startDate ? format(new Date(filters.startDate), "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={new Date(filters.startDate)}
                      onSelect={(date) => setFilters({
                        ...filters, 
                        startDate: date ? format(date, 'yyyy-MM-dd') : format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd')
                      })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !filters.endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.endDate ? format(new Date(filters.endDate), "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={new Date(filters.endDate)}
                      onSelect={(date) => setFilters({
                        ...filters, 
                        endDate: date ? format(date, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')
                      })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="verifiedFilter">Verification Status</Label>
                <Select 
                  value={filters.isVerified === undefined ? '' : String(filters.isVerified)} 
                  onValueChange={(value) => {
                    const isVerified = value === '' ? undefined : value === 'true';
                    setFilters({...filters, isVerified});
                  }}
                >
                  <SelectTrigger id="verifiedFilter">
                    <SelectValue placeholder="Any Verification Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any Verification Status</SelectItem>
                    <SelectItem value="true">Verified Only</SelectItem>
                    <SelectItem value="false">Unverified Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="reportedFilter">Reporting Status</Label>
                <Select 
                  value={filters.isReported === undefined ? '' : String(filters.isReported)} 
                  onValueChange={(value) => {
                    const isReported = value === '' ? undefined : value === 'true';
                    setFilters({...filters, isReported});
                  }}
                >
                  <SelectTrigger id="reportedFilter">
                    <SelectValue placeholder="Any Reporting Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any Reporting Status</SelectItem>
                    <SelectItem value="true">Reported Only</SelectItem>
                    <SelectItem value="false">Unreported Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2 flex items-center space-x-2">
                <Switch 
                  id="saveTemplate"
                  checked={saveTemplate}
                  onCheckedChange={setSaveTemplate}
                />
                <Label htmlFor="saveTemplate">Save as template</Label>
                
                {saveTemplate && (
                  <div className="flex-1 ml-4">
                    <Input
                      placeholder="Template Name"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="regulatory" className="space-y-4">
            {/* Regulatory Export Options */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="regStateFilter">State</Label>
                <Select 
                  value={filters.state || ''} 
                  onValueChange={(value) => setFilters({...filters, state: value || undefined})}
                >
                  <SelectTrigger id="regStateFilter">
                    <SelectValue placeholder="Select State" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FL">Florida</SelectItem>
                    <SelectItem value="CA">California</SelectItem>
                    <SelectItem value="TX">Texas</SelectItem>
                    <SelectItem value="NY">New York</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="regCourseType">Course Type</Label>
                <Select 
                  value={filters.courseType || ''} 
                  onValueChange={(value) => setFilters({...filters, courseType: value || undefined})}
                >
                  <SelectTrigger id="regCourseType">
                    <SelectValue placeholder="Select Course Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BDI">BDI</SelectItem>
                    <SelectItem value="ADI">ADI</SelectItem>
                    <SelectItem value="TLSAE">TLSAE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !filters.startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.startDate ? format(new Date(filters.startDate), "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={new Date(filters.startDate)}
                      onSelect={(date) => setFilters({
                        ...filters, 
                        startDate: date ? format(date, 'yyyy-MM-dd') : format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd')
                      })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !filters.endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.endDate ? format(new Date(filters.endDate), "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={new Date(filters.endDate)}
                      onSelect={(date) => setFilters({
                        ...filters, 
                        endDate: date ? format(date, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')
                      })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            <div className="border rounded-md p-4 space-y-4">
              <h3 className="text-lg font-medium">Regulatory Report Settings</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="includePII"
                    checked={regulatorySettings.includePII}
                    onCheckedChange={(checked) => setRegulatorySettings({...regulatorySettings, includePII: checked})}
                  />
                  <Label htmlFor="includePII">Include PII (Personal Identifiable Information)</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="includeAddress"
                    checked={regulatorySettings.includeStudentAddress}
                    onCheckedChange={(checked) => setRegulatorySettings({...regulatorySettings, includeStudentAddress: checked})}
                  />
                  <Label htmlFor="includeAddress">Include Student Address</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="includeCompletion"
                    checked={regulatorySettings.includeCompletionStatus}
                    onCheckedChange={(checked) => setRegulatorySettings({...regulatorySettings, includeCompletionStatus: checked})}
                  />
                  <Label htmlFor="includeCompletion">Include Completion Status</Label>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="formatVersion">Format Version</Label>
                  <Select 
                    value={regulatorySettings.formatVersion} 
                    onValueChange={(value) => setRegulatorySettings({...regulatorySettings, formatVersion: value})}
                  >
                    <SelectTrigger id="formatVersion">
                      <SelectValue placeholder="Select format version" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1.0">1.0 (Legacy)</SelectItem>
                      <SelectItem value="2.0">2.0 (Standard)</SelectItem>
                      <SelectItem value="2.1">2.1 (Current)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="signatureRequired"
                    checked={regulatorySettings.signatureRequired}
                    onCheckedChange={(checked) => setRegulatorySettings({...regulatorySettings, signatureRequired: checked})}
                  />
                  <Label htmlFor="signatureRequired">Digital Signature Required</Label>
                </div>
              </div>
              
              {regulatorySettings.signatureRequired && (
                <div className="space-y-2">
                  <Label htmlFor="signatureText">Signature Text</Label>
                  <Input
                    id="signatureText"
                    value={regulatorySettings.signatureText}
                    onChange={(e) => setRegulatorySettings({...regulatorySettings, signatureText: e.target.value})}
                  />
                </div>
              )}
              
              <div className="col-span-2 flex items-center space-x-2 pt-2">
                <Switch 
                  id="saveRegTemplate"
                  checked={saveTemplate}
                  onCheckedChange={setSaveTemplate}
                />
                <Label htmlFor="saveRegTemplate">Save as template</Label>
                
                {saveTemplate && (
                  <div className="flex-1 ml-4">
                    <Input
                      placeholder="Template Name"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="templates" className="space-y-4">
            {/* Saved Templates List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {savedTemplates?.map((template) => (
                <Card key={template.id} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="p-4 border-b">
                      <h3 className="font-medium">{template.name}</h3>
                      <p className="text-sm text-muted-foreground capitalize">
                        {template.format} Export
                        {template.regulatory_settings ? ' (Regulatory)' : ''}
                      </p>
                    </div>
                    <div className="p-4 text-sm">
                      <div className="flex justify-between mb-1">
                        <span className="text-muted-foreground">State:</span>
                        <span>{template.filters?.state || 'Any'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Course Type:</span>
                        <span>{template.filters?.courseType || 'Any'}</span>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <Button 
                          size="sm"
                          variant="outline" 
                          onClick={() => handleLoadTemplate(template.id)}
                          className="w-full"
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          Load Template
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {(!savedTemplates || savedTemplates.length === 0) && (
                <div className="col-span-2 text-center py-8 text-muted-foreground">
                  <FileText className="mx-auto h-8 w-8 mb-2" />
                  <p>No saved templates found</p>
                  <p className="text-sm">Save a template from the Standard or Regulatory tabs</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="flex items-center">
          {saveTemplate ? (
            <Button type="button" onClick={handleSaveTemplate} className="flex items-center">
              <Save className="mr-2 h-4 w-4" />
              Save Template
            </Button>
          ) : (
            <Button type="button" onClick={handleExport} className="flex items-center">
              <Download className="mr-2 h-4 w-4" />
              Export Now
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
